import {
  Bookmark,
  BrainCircuit,
  ChevronDown,
  Crown,
  FileText,
  Compass,
  GraduationCap,
  GitBranch,
  Home,
  Languages,
  LifeBuoy,
  LogIn,
  LogOut,
  Menu,
  MoreHorizontal,
  MessagesSquare,
  Shield,
  Sparkles,
  Trophy,
  UserPlus,
  UserRound,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { Toaster } from 'sonner'
import { useAuth } from '../auth-context.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import AIPetWidget from './AIPetWidget.jsx'
import FeedbackWidget from './FeedbackWidget.jsx'

const BASE_NAV_ITEMS = [
  { to: '/', labelKey: 'nav.navHome', icon: Home, end: true },
  { to: '/growth', labelKey: 'nav.navGrowth', icon: GraduationCap },
  { to: '/explore', labelKey: 'nav.navExplore', icon: Compass },
  { to: '/projects', labelKey: 'nav.navProjectsResume', icon: FileText },
  { to: '/ai-partner', labelKey: 'nav.navAiPartner', icon: BrainCircuit },
]

const MOBILE_NAV_ITEMS = [
  { to: '/', labelKey: 'nav.navHome', icon: Home, end: true },
  { to: '/growth', labelKey: 'nav.navGrowth', icon: GraduationCap },
  { to: '/explore', labelKey: 'nav.navExplore', icon: Compass },
  { to: '/projects', labelKey: 'nav.navProjectsResume', icon: FileText },
  { to: '/ai-partner', labelKey: 'nav.navAiPartner', icon: BrainCircuit },
  { to: '/more', labelKey: 'nav.navMore', icon: MoreHorizontal },
]

const MORE_ITEMS = [
  { to: '/more/competitions', labelKey: 'moreMenu.competitions', descKey: 'moreMenu.competitionsDesc', icon: Trophy },
  { to: '/more/certificates', labelKey: 'moreMenu.certificates', descKey: 'moreMenu.certificatesDesc', icon: Bookmark },
  { to: '/more/interview', labelKey: 'moreMenu.interview', descKey: 'moreMenu.interviewDesc', icon: MessagesSquare },
  { to: '/more/resume', labelKey: 'moreMenu.resume', descKey: 'moreMenu.resumeDesc', icon: FileText },
  { to: '/more/ai-tools', labelKey: 'moreMenu.aiTools', descKey: 'moreMenu.aiToolsDesc', icon: BrainCircuit },
  { to: '/weekly', labelKey: 'moreMenu.dailyRanking', descKey: 'moreMenu.dailyRankingDesc', icon: Sparkles },
  { to: '/all-time', labelKey: 'moreMenu.allTimeRanking', descKey: 'moreMenu.allTimeRankingDesc', icon: GitBranch },
  { to: '/tools', labelKey: 'moreMenu.todayPicks', descKey: 'moreMenu.todayPicksDesc', icon: Sparkles },
  { to: '/github-analysis', labelKey: 'moreMenu.githubAnalysis', descKey: 'moreMenu.githubAnalysisDesc', icon: BrainCircuit },
  { to: '/vip', labelKey: 'moreMenu.vip', descKey: 'moreMenu.vipDesc', icon: Crown },
]

function localizedItems(items, t) {
  return items.map((item) => ({
    ...item,
    label: t(item.labelKey),
    description: item.descKey ? t(item.descKey) : '',
  }))
}

function NavItem({ item, onClick }) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      draggable={false}
      onDragStart={(event) => event.preventDefault()}
    >
      <Icon size={16} />
      <span>{item.label}</span>
    </NavLink>
  )
}

function MorePanelItem({ item, onSelect }) {
  const Icon = item.icon
  return (
    <button type="button" className="more-panel-item" onClick={() => onSelect(item.to)}>
      <span className="more-panel-item-icon"><Icon size={18} /></span>
      <span>
        <strong>{item.label}</strong>
        <small>{item.description}</small>
      </span>
    </button>
  )
}

function userDisplayName(user, t) {
  return user?.username || user?.email || t('common.myAccount')
}

function isAdminUser(user) {
  return user?.isAdmin === true || user?.is_admin === true
}

function AppLayout() {
  const { user, logout } = useAuth()
  const { language, setLanguage, t } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const accountMenuRef = useRef(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [accountMenuOpen, setAccountMenuOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [assistantRepo, setAssistantRepo] = useState(null)
  const outletContext = useMemo(() => ({
    setAssistantRepo,
  }), [])
  const isAdmin = isAdminUser(user)

  const navItems = useMemo(() => localizedItems(BASE_NAV_ITEMS, t), [t])
  const mobileNavItems = useMemo(() => localizedItems(MOBILE_NAV_ITEMS, t), [t])
  const moreItems = useMemo(() => {
    const items = localizedItems(MORE_ITEMS, t)
    items.push({
      to: '/support',
      label: t('moreMenu.support'),
      description: t('moreMenu.supportDesc'),
      icon: LifeBuoy,
    })
    items.push({
      to: user ? '/profile' : '/login',
      label: t('moreMenu.profile'),
      description: user ? t('moreMenu.profileDesc') : t('moreMenu.loginProfileDesc'),
      icon: UserRound,
    })
    if (isAdmin) {
      items.push({
        to: '/admin',
        label: t('moreMenu.admin'),
        description: t('moreMenu.adminDesc'),
        icon: Shield,
      })
    }
    return items
  }, [isAdmin, t, user])

  useEffect(() => {
    if (!location.pathname.startsWith('/repos/')) setAssistantRepo(null)
  }, [location.pathname])

  useEffect(() => {
    setMobileMenuOpen(false)
    setAccountMenuOpen(false)
    setMoreOpen(false)
  }, [location.pathname, location.hash])

  useEffect(() => {
    if (!accountMenuOpen) return undefined

    const handlePointerDown = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) setAccountMenuOpen(false)
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setAccountMenuOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [accountMenuOpen])

  useEffect(() => {
    if (!moreOpen) return undefined
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMoreOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [moreOpen])

  const closeMenus = () => {
    setMobileMenuOpen(false)
    setAccountMenuOpen(false)
    setMoreOpen(false)
  }

  const handleLogout = () => {
    logout()
    closeMenus()
    navigate('/')
  }

  const displayName = userDisplayName(user, t)
  const avatarText = displayName.trim().slice(0, 1).toUpperCase() || 'U'
  const avatarUrl = user?.avatarUrl || user?.avatar_url || ''
  const languageLabel = language === 'zh' ? t('header.languageSwitchToEnglish') : t('header.languageSwitchToChinese')
  const switchLanguage = (nextLanguage) => {
    setLanguage(nextLanguage)
    closeMenus()
  }
  const openMorePanel = (event) => {
    event?.preventDefault()
    event?.stopPropagation()
    setAccountMenuOpen(false)
    setMobileMenuOpen(false)
    setMoreOpen(true)
  }
  const handleMoreSelect = (to) => {
    setMoreOpen(false)
    navigate(to)
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-inner">
          <NavLink to="/" className="brand" onClick={closeMenus} aria-label={t('common.backHome')} draggable={false} onDragStart={(event) => event.preventDefault()}>
            <span className="brand-icon"><img src="/brand/qingtu-logo.png" alt="" /></span>
            <span className="brand-copy">
              <strong>{t('nav.brand')}</strong>
              <small>{t('nav.brandSub')}</small>
            </span>
          </NavLink>

          <nav className="main-nav desktop-nav" aria-label={t('nav.aria')}>
            {navItems.map((item) => (
              <NavItem item={item} key={item.to} onClick={closeMenus} />
            ))}
          </nav>

          <div className="header-actions">
            <div className="nav-more-menu header-more-menu">
              <button
                type="button"
                onClick={openMorePanel}
                aria-expanded={moreOpen}
                aria-haspopup="dialog"
              >
                <MoreHorizontal size={16} />
                <span>{t('nav.more')}</span>
              </button>
            </div>

            {user ? (
              <div className="user-menu" ref={accountMenuRef}>
                <button
                  className="user-menu-trigger"
                  type="button"
                  onClick={() => setAccountMenuOpen((open) => !open)}
                  aria-expanded={accountMenuOpen}
                  aria-haspopup="menu"
                >
                  {avatarUrl ? (
                    <img className="user-avatar user-avatar-image" src={avatarUrl} alt="" />
                  ) : (
                    <span className="user-avatar" aria-hidden="true">{avatarText}</span>
                  )}
                  <span className="user-menu-name">{displayName}</span>
                  {isAdmin && <span className="user-role-pill">{t('common.manageAdmin')}</span>}
                  <ChevronDown size={15} />
                </button>

                {accountMenuOpen && (
                  <div className="user-dropdown" role="menu">
                    <NavLink to="/profile" role="menuitem" onClick={closeMenus} draggable={false} onDragStart={(event) => event.preventDefault()}>
                      <UserRound size={16} />
                      {t('common.userCenter')}
                    </NavLink>
                    <NavLink to="/favorites" role="menuitem" onClick={closeMenus} draggable={false} onDragStart={(event) => event.preventDefault()}>
                      <Bookmark size={16} />
                      {t('common.myFavorites')}
                    </NavLink>
                    <NavLink to="/my-github" role="menuitem" onClick={closeMenus} draggable={false} onDragStart={(event) => event.preventDefault()}>
                      <GitBranch size={16} />
                      {t('common.myGithub')}
                    </NavLink>
                    {isAdmin && (
                      <NavLink to="/admin" role="menuitem" onClick={closeMenus} draggable={false} onDragStart={(event) => event.preventDefault()}>
                        <Shield size={16} />
                        {t('common.manageAdmin')}
                      </NavLink>
                    )}
                    <button type="button" role="menuitem" onClick={() => switchLanguage(language === 'zh' ? 'en' : 'zh')}>
                      <Languages size={16} />
                      {languageLabel}
                    </button>
                    <button type="button" role="menuitem" onClick={handleLogout}>
                      <LogOut size={16} />
                      {t('common.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-actions">
                <button
                  className="secondary-button language-toggle"
                  type="button"
                  onClick={() => switchLanguage(language === 'zh' ? 'en' : 'zh')}
                  aria-label={t('nav.language')}
                >
                  {languageLabel}
                </button>
                <NavLink to="/login" className="secondary-button" onClick={closeMenus} draggable={false} onDragStart={(event) => event.preventDefault()}>
                  <LogIn size={16} />
                  {t('common.login')}
                </NavLink>
                <NavLink to="/register" className="primary-button" onClick={closeMenus} draggable={false} onDragStart={(event) => event.preventDefault()}>
                  <UserPlus size={16} />
                  {t('common.register')}
                </NavLink>
              </div>
            )}

            {user && (
              <button
                className="secondary-button language-toggle desktop-language-toggle"
                type="button"
                onClick={() => switchLanguage(language === 'zh' ? 'en' : 'zh')}
                aria-label={t('nav.language')}
              >
                {languageLabel}
              </button>
            )}

            <button
              className="icon-button mobile-menu-button"
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <nav className="mobile-nav" aria-label={t('nav.mobileAria')}>
            <div className="mobile-nav-inner">
              <button
                className="mobile-language-toggle"
                type="button"
                onClick={() => switchLanguage(language === 'zh' ? 'en' : 'zh')}
              >
                {languageLabel}
              </button>
              <div className="mobile-nav-divider" />
              {user ? (
                <>
                  <NavLink to="/profile" onClick={closeMenus} draggable={false} onDragStart={(event) => event.preventDefault()}>
                    <UserRound size={16} />
                    {t('common.userCenter')}
                  </NavLink>
                  {isAdmin && (
                    <NavLink to="/admin" onClick={closeMenus} draggable={false} onDragStart={(event) => event.preventDefault()}>
                      <Shield size={16} />
                      {t('common.manageAdmin')}
                    </NavLink>
                  )}
                  <button className="mobile-logout-button" type="button" onClick={handleLogout}>
                    <LogOut size={16} />
                    {t('common.logout')}
                  </button>
                </>
              ) : (
                <>
                  <NavLink to="/login" onClick={closeMenus} draggable={false} onDragStart={(event) => event.preventDefault()}>
                    <LogIn size={16} />
                    {t('common.login')}
                  </NavLink>
                  <NavLink to="/register" onClick={closeMenus} draggable={false} onDragStart={(event) => event.preventDefault()}>
                    <UserPlus size={16} />
                    {t('common.register')}
                  </NavLink>
                </>
              )}
            </div>
          </nav>
        )}
      </header>

      <nav className="mobile-bottom-nav" aria-label={t('nav.mobileAria')}>
        {mobileNavItems.map((item) => (
          <NavItem item={item} key={item.to} onClick={closeMenus} />
        ))}
      </nav>

      {moreOpen && (
        <div className="more-panel-backdrop" onClick={() => setMoreOpen(false)}>
          <nav className="more-panel" aria-label={t('nav.more')} onClick={(event) => event.stopPropagation()}>
            <header>
              <strong>{t('moreMenu.title')}</strong>
              <button type="button" onClick={() => setMoreOpen(false)} aria-label={t('nav.closeMenu')}>
                <X size={18} />
              </button>
            </header>
            <div className="more-panel-list">
              {moreItems.map((item) => (
                <MorePanelItem item={item} key={`${item.to}-${item.label}`} onSelect={handleMoreSelect} />
              ))}
            </div>
          </nav>
        </div>
      )}

      <main className="page-main">
        <Outlet context={outletContext} />
      </main>
      {!location.pathname.startsWith('/admin') && !location.pathname.startsWith('/growth/explore/') && (
        <AIPetWidget repoContext={assistantRepo} />
      )}
      <FeedbackWidget />
      <Toaster richColors position="top-center" />
    </div>
  )
}

export default AppLayout
